import { useMemo } from 'react';
import { useMLMLAgentInfo } from 'hooks/use-mlml-agent-info';
import { isExpListing } from 'utils/is-exp-listing';
import { isQuebecListing } from 'utils/is-quebec-listing';
import type Listing from 'data/listing';

export interface MyListingAgent {
  name: string;
  email: string;
  phone: string;
  photo: string;
  designation?: string;
  agentKeyNumeric?: number | null;
  isMlmlAgent: boolean;
}

interface UseMyListingAgentsReturn {
  agents: MyListingAgent[];
  isLoading: boolean;
  shouldShow: boolean;
}

/**
 * Resolves the agent(s) to display on a listing page based on the
 * My Link My Lead / My Listing My Lead priority hierarchy:
 *
 * 1. If MLML lead cookie exists → show only the MLML agent (with "Courtesy of" label), except in
 *    Quebec where listing agents from agentsInfo are also shown (carousel) for regional compliance.
 * 2. If no MLML cookie and listing is eXp → show listing agent(s) from agentsInfo only (client-app
 *    or go-search listing payload)
 * 3. If no MLML cookie and listing is Quebec (non–eXp) → still show listing agent(s) from agentsInfo
 *    when present (regional compliance)
 * 4. Otherwise → show nothing
 */
export function useMyListingAgents(listing: Listing): UseMyListingAgentsReturn {
  const { agentInfo: mlmlAgent } = useMLMLAgentInfo();
  const hasMLMLAgent = !!mlmlAgent?.firstName;
  const isExp = isExpListing(listing);
  const isQuebec = isQuebecListing(listing.province);
  const includeListingAgentsWithMlml = !hasMLMLAgent || isQuebec;

  const agents = useMemo<MyListingAgent[]>(() => {
    const result: MyListingAgent[] = [];

    if (hasMLMLAgent) {
      result.push({
        name: `${mlmlAgent.firstName} ${mlmlAgent.lastName}`,
        email: mlmlAgent.email || '',
        phone: mlmlAgent.phoneNumber || '',
        photo: mlmlAgent.photo || '',
        isMlmlAgent: true,
      });
    }

    if (includeListingAgentsWithMlml && listing.agentsInfo?.length) {
      for (const [index, agent] of listing.agentsInfo.entries()) {
        if (!agent.name && !agent.email && !agent.phone) continue;

        // Prefer per-agent numeric key, but fall back to listing-level primary key
        // for go-search payloads where only `expAgentId` may be present.
        const agentKeyNumeric =
          agent.exp_member_key ?? (index === 0 ? listing.expAgentId ?? null : null);

        result.push({
          name: agent.name,
          email: agent.email,
          phone: agent.phone,
          photo: agent.photo,
          designation: agent.designation,
          agentKeyNumeric,
          isMlmlAgent: false,
        });
      }
    }

    return result;
  }, [hasMLMLAgent, includeListingAgentsWithMlml, mlmlAgent, listing.agentsInfo]);

  // Show when: MLML session; or we have listing agents and the listing is eXp or Quebec.
  const shouldShow =
    hasMLMLAgent || (agents.length > 0 && (isExp || isQuebec));

  return {
    agents,
    isLoading: false,
    shouldShow,
  };
}
